import axiosConfig from "./axiosConfig.js";
import { normalizeCartResponse } from "../Utils/normalizeCartResponse.js";
import { getGuestToken, setGuestToken } from "../Utils/guestToken.js";

/**
 * Builds the official cart URL with query parameters
 * Passes guest_token and store in the query string as well as headers
 */
const buildCartUrl = () => {
  const guestToken = getGuestToken();
  const storeId = Number(localStorage.getItem("storeId")) || 7;
  const params = [];
  if (storeId) params.push(`store=${storeId}`);
  if (guestToken) params.push(`guest_token=${guestToken}`);
  return `/accounts/api/cart/${params.length > 0 ? `?${params.join("&")}` : ""}`;
};

/**
 * Helper to fetch full order details for a cart order (fallback if /accounts/api/cart/ is 403)
 */
const fetchCartOrderDetails = async (cartOrder) => {
  if (!cartOrder || !cartOrder.id) {
    return normalizeCartResponse(null);
  }
  try {
    const rawStore = cartOrder.store;
    const fallbackStoreId = typeof rawStore === "object" && rawStore !== null ? rawStore.id : rawStore;
    const storeId = Number(localStorage.getItem("storeId")) || Number(fallbackStoreId) || 7;
    const detailsRes = await axiosConfig.get(
      `/accounts/order-details/?order=${cartOrder.id}&page_size=1000&store=${storeId}`
    );
    const items = detailsRes.data?.results || detailsRes.data || [];
    return normalizeCartResponse({
      ...cartOrder,
      items,
    });
  } catch (error) {
    console.warn("Failed to fetch cart order details fallback:", error);
    return normalizeCartResponse(cartOrder);
  }
};

// Module-level mutation queue and authoritative version tracker
let mutationQueue = Promise.resolve();
let latestAuthoritativeVersion = null;

/**
 * Fetch current active cart (Official GET /accounts/api/cart/ with fallback)
 */
export const fetchCart = async () => {
  const token = localStorage.getItem("token");
  const guestToken = getGuestToken();

  // If no auth token and no guest token, there is no server cart yet
  if (!token && !guestToken) {
    return normalizeCartResponse(null);
  }

  // 1. Primary: Official GET /accounts/api/cart/?guest_token=...&store=...
  // Sends guest_token in both query param and X-Guest-Token header
  try {
    const res = await axiosConfig.get(buildCartUrl());
    if (res.data && res.data.data) {
      const normalized = normalizeCartResponse(res.data);
      // Backend GET /accounts/api/cart/ currently returns static version: 1.
      // Query active cart order to get the true database version if version returned is 1
      if (normalized.version === 1) {
        try {
          const orderRes = await axiosConfig.get("/accounts/orders/?orderstatus=Cart");
          const orders = orderRes.data?.results || (Array.isArray(orderRes.data) ? orderRes.data : []);
          if (orders.length > 0 && orders[0].version) {
            normalized.version = Number(orders[0].version);
          }
        } catch (err) {}
      }
      if (normalized.version) {
        latestAuthoritativeVersion = Number(normalized.version);
      }
      return normalized;
    }
  } catch (error) {
    // If 403 (unauthenticated guest waiting for backend AllowAny permission fix), use fallback
    if (error?.response?.status !== 403) {
      console.warn("GET /accounts/api/cart/ error:", error);
    }
  }

  // 2. Fallback: GET /accounts/orders/?orderstatus=Cart
  try {
    const res = await axiosConfig.get("/accounts/orders/?orderstatus=Cart");
    const cartOrders = res.data?.results || (Array.isArray(res.data) ? res.data : []);

    if (cartOrders.length > 0) {
      const activeCart = cartOrders[0];
      const details = await fetchCartOrderDetails(activeCart);
      if (details?.version) {
        latestAuthoritativeVersion = Number(details.version);
      }
      return details;
    }

    return normalizeCartResponse(null);
  } catch (error) {
    console.warn("fetchCart fallback error:", error);
    return normalizeCartResponse(null);
  }
};

/**
 * Inner execution helper for cart mutations (/accounts/api/cart/mutate/)
 * 1. Resolves effective version using Math.max(currentVersion, latestAuthoritativeVersion)
 * 2. Attaches If-Match optimistic concurrency header
 * 3. Auto-captures backend-generated guest token
 * 4. Transparently auto-recovers from 409 CART_VERSION_CONFLICT up to 3 times
 * 5. Updates and preserves authoritative DB version returned from mutate response
 */
const executeMutateCart = async (payload, currentVersion, retryCount = 0) => {
  const effectiveVersion = Math.max(
    Number(currentVersion) || 1,
    Number(latestAuthoritativeVersion) || 1
  );

  const config = {};
  if (effectiveVersion > 0) {
    config.headers = { "If-Match": `${effectiveVersion}` };
  }

  let res;
  try {
    res = await axiosConfig.post("/accounts/api/cart/mutate/", payload, config);
  } catch (error) {
    const serverCurrentVersion =
      error.currentVersion ||
      error.response?.data?.error?.current_version ||
      error.response?.data?.current_version;

    if (
      (error.status === 409 || error.response?.status === 409 || error.code === "CART_VERSION_CONFLICT") &&
      serverCurrentVersion &&
      retryCount < 3
    ) {
      latestAuthoritativeVersion = Number(serverCurrentVersion);
      console.warn(
        `[Auto-Recovery] Cart version conflict (sent ${effectiveVersion}, server is at ${serverCurrentVersion}). Retrying attempt ${retryCount + 1}/3...`
      );
      return await executeMutateCart(payload, serverCurrentVersion, retryCount + 1);
    }
    throw error;
  }

  // Auto-capture backend-generated guest token
  if (res.data?.guest_token) {
    setGuestToken(res.data.guest_token);
  }

  // Authoritative version directly from mutate response
  const authoritativeVersion = res.data?.version;
  if (authoritativeVersion) {
    latestAuthoritativeVersion = Number(authoritativeVersion);
  }

  if (res.data?.cart === null || res.data?.message?.includes("empty")) {
    const normalized = normalizeCartResponse(res.data);
    if (authoritativeVersion) normalized.version = authoritativeVersion;
    return normalized;
  }

  // Try official get_cart first to get item details, then fallback
  try {
    const freshCart = await axiosConfig.get(buildCartUrl());
    if (freshCart.data && freshCart.data.data) {
      const normalized = normalizeCartResponse(freshCart.data);
      // Preserve the authoritative DB version from mutate response!
      if (authoritativeVersion) {
        normalized.version = authoritativeVersion;
      }
      return normalized;
    }
  } catch (e) {}

  const orderNormalized = await fetchCartOrderDetails(res.data);
  if (authoritativeVersion) {
    orderNormalized.version = authoritativeVersion;
  }
  return orderNormalized;
};

/**
 * Shared helper for all cart mutations (/accounts/api/cart/mutate/)
 * Queues mutations sequentially through a Promise mutex to ensure
 * ascending version numbers and eliminate parallel 409 CART_VERSION_CONFLICTs.
 */
const mutateCart = (payload, currentVersion) => {
  const run = async () => {
    return await executeMutateCart(payload, currentVersion);
  };
  const task = mutationQueue.catch(() => {}).then(run);
  mutationQueue = task;
  return task;
};

/**
 * Add product to cart via server mutation
 */
export const addItem = async (productId, quantity = 1, currentVersion) => {
  const storeId = Number(localStorage.getItem("storeId")) || 7;
  return await mutateCart(
    {
      action: "add",
      product_id: productId,
      quantity,
      store_id: storeId,
    },
    currentVersion
  );
};

/**
 * Update product quantity in cart via server mutation
 */
export const updateItem = async (productId, quantity, currentVersion) => {
  const storeId = Number(localStorage.getItem("storeId")) || 7;
  return await mutateCart(
    {
      action: "update",
      product_id: productId,
      quantity,
      store_id: storeId,
    },
    currentVersion
  );
};

/**
 * Remove product from cart via server mutation
 */
export const removeItem = async (productId, currentVersion) => {
  const storeId = Number(localStorage.getItem("storeId")) || 7;
  return await mutateCart(
    {
      action: "remove",
      product_id: productId,
      store_id: storeId,
    },
    currentVersion
  );
};

/**
 * Merge guest cart into customer cart
 */
export const mergeCart = async (guestToken) => {
  // Merging happens automatically via verify_otp with guest_token
  return await fetchCart();
};

export default {
  fetchCart,
  addItem,
  updateItem,
  removeItem,
  mergeCart,
};
